import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { getCSRFToken } from './utils/csrf';

// Custom hook for making authenticated API calls with automatic token refresh
export function useAuthenticatedApi() {
  const { getAccessTokenSilently, isAuthenticated, loginWithRedirect } = useAuth0();

  const authenticatedFetch = useCallback(async (url, options = {}, retryCount = 0) => {
    try {
      // Get a fresh access token (Auth0 will automatically refresh if needed)
      // Add timeout to prevent hanging
      const tokenPromise = getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
      });
      const tokenTimeout = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Token fetch timeout')), 5000)
      );
      const token = await Promise.race([tokenPromise, tokenTimeout]);

      // Prepare headers
      const headers = {
        Authorization: `Bearer ${token}`,
        ...options.headers,
      };

      // Don't set Content-Type for FormData - browser will set it automatically with boundary
      if (!(options.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
      }

      // GET requests don't need CSRF protection, skip CSRF token for GET requests
      const isGetRequest = !options.method || options.method.toUpperCase() === 'GET';
      if (!isGetRequest) {
        // Get CSRF token and add it to headers (only for non-GET requests)
        try {
          const csrfToken = await getCSRFToken(retryCount > 0); // Force refresh on retry
          headers['X-CSRF-Token'] = csrfToken;
        } catch (csrfError) {
          // If CSRF token fetch fails for non-GET requests, we need it, so throw the error
          throw csrfError;
        }
      }

      // Determine timeout based on endpoint - filters + forum thread list can be slow on large DBs
      const isFiltersEndpoint = url.includes('/opportunities/filters') || url.includes('/filters');
      const isForumThreadsList = url.includes('/forum/threads');
      // List endpoint only (not /opportunities/:hash detail or /filters)
      const pathNoQuery = url.split('?')[0];
      const isOpportunitiesList =
        pathNoQuery.endsWith('/opportunities') || pathNoQuery.endsWith('/api/opportunities');
      const requestTimeout = isForumThreadsList
        ? 120000
        : isOpportunitiesList
          ? 120000
          : isFiltersEndpoint
            ? 20000
            : 10000;

      // Make the request with the token and CSRF protection
      // cache: 'no-store' prevents 304 responses with empty body, which break response.json()
      const fetchPromise = fetch(url, {
        ...options,
        headers,
        credentials: 'include', // Include cookies for session
        cache: options.cache ?? 'no-store',
      });
      
      const fetchTimeout = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), requestTimeout)
      );
      
      const response = await Promise.race([fetchPromise, fetchTimeout]);

      // If we get a 403 and haven't retried yet, retry with a fresh CSRF token
      if (response.status === 403 && retryCount === 0) {
        return authenticatedFetch(url, options, retryCount + 1);
      }
      
      // If we get a 500/502/503/504 and haven't retried yet, retry once (network/server errors)
      if ((response.status === 500 || response.status === 502 || response.status === 503 || response.status === 504) && retryCount < 2) {
        // Wait a bit before retrying (shorter delay for faster retries)
        await new Promise(resolve => setTimeout(resolve, 300 * (retryCount + 1)));
        return authenticatedFetch(url, options, retryCount + 1);
      }

      return response;
    } catch (error) {
      // Check if this is a refresh token error that requires re-authentication
      const errorMessage = error?.message || error?.error_description || String(error || '');
      const errorCode = error?.error || '';
      
      const isRefreshTokenError = 
        errorCode === 'login_required' ||
        errorCode === 'consent_required' ||
        errorCode === 'interaction_required' ||
        errorCode === 'invalid_grant' ||
        errorMessage.includes('login_required') ||
        errorMessage.includes('refresh_token') ||
        errorMessage.includes('Invalid refresh token') ||
        errorMessage.includes('Unknown or invalid refresh token') ||
        errorMessage.includes('invalid_grant');

      if (isRefreshTokenError && retryCount === 0) {
        
        // Clear Auth0 cache from localStorage to force fresh login
        try {
          const auth0Keys = Object.keys(localStorage).filter(key => 
            key.startsWith('@@auth0spajs@@') || key.includes('auth0')
          );
          auth0Keys.forEach(key => localStorage.removeItem(key));
        } catch (e) {
          // Failed to clear Auth0 cache - continue anyway
        }

        // A stale/expired session leaves Auth0 reporting `isAuthenticated` (from the
        // cached token) while the refresh token is dead. Re-confirm with Auth0 instead of
        // returning a 401 that callers (e.g. subscription check) misread as "no subscription"
        // and bounce to /plans. Skip only while Auth0 is processing its own redirect callback
        // (?code=&state=), to avoid interrupting the login round-trip.
        const params = new URLSearchParams(window.location.search);
        const isAuth0Callback = params.has('code') && params.has('state');
        if (isAuthenticated && !isAuth0Callback) {
          // Use setTimeout to avoid blocking the current execution
          setTimeout(() => {
            loginWithRedirect({
              appState: {
                returnTo: window.location.pathname + window.location.search,
              },
            }).catch(err => {
              console.error('Failed to redirect to login:', err);
            });
          }, 100);
        }

        // Return a response that indicates authentication is needed
        // Components should handle 401 responses gracefully
        return new Response(
          JSON.stringify({ 
            error: 'Your session has expired. Please log in again.',
            code: 'AUTH_REQUIRED'
          }),
          {
            status: 401,
            headers: { 'Content-Type': 'application/json' },
          }
        );
      }

      // For timeout errors, retry once if we haven't already
      if ((error.message === 'Request timeout' || error.message === 'Token fetch timeout') && retryCount < 2) {
        // Wait a bit before retrying (shorter delay for faster retries)
        await new Promise(resolve => setTimeout(resolve, 300 * (retryCount + 1)));
        return authenticatedFetch(url, options, retryCount + 1);
      }

      // For other errors, re-throw
      throw error;
    }
  }, [getAccessTokenSilently, loginWithRedirect, isAuthenticated]);

  // Helper for common API patterns
  const api = useCallback(() => ({
    get: (url) => authenticatedFetch(url),
    post: (url, data) => authenticatedFetch(url, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    put: (url, data) => authenticatedFetch(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
    delete: (url) => authenticatedFetch(url, {
      method: 'DELETE',
    }),
  }), [authenticatedFetch]);

  return { authenticatedFetch, api, isAuthenticated };
}
